// ** React Imports
// ** Custom Components
import Customizer from "@components/customizer";
// ** Configs
import themeConfig from "@configs/themeConfig";
import { useFooterType } from "@hooks/useFooterType";
import { useNavbarColor } from "@hooks/useNavbarColor";
import { useNavbarType } from "@hooks/useNavbarType";
// ** Custom Hooks
import { useRTL } from "@hooks/useRTL";
import { useSkin } from "@hooks/useSkin";
import {
    handleContentWidth,
    handleMenuCollapsed,
    handleMenuHidden,
} from "@store/actions/layout";
// ** Styles
import "@styles/base/core/menu/menu-types/vertical-menu.scss";
import "@styles/base/core/menu/menu-types/vertical-overlay-menu.scss";
// ** Third Party Components
import classnames from "classnames";
import { useEffect, useState } from "react";
import { ArrowUp } from "react-feather";
// ** Store & Actions
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import ScrollToTop from "react-scroll-up";
import { Button, Navbar } from "reactstrap";
import FooterComponent from "./components/footer";
import SidebarComponent from "./components/menu/vertical-menu";
import NavbarComponent from "./components/navbar";
import jeeves from "../../assets/images/avatars/jeeves.png";

const VerticalLayout = (props) => {
    // ** Props
    const { children, navbar, footer, menu, routerProps, currentActiveItem } =
        props;

    // ** Hooks
    const [skin, setSkin] = useSkin();
    const [isRtl, setIsRtl] = useRTL();
    const [navbarType, setNavbarType] = useNavbarType();
    const [footerType, setFooterType] = useFooterType();
    const [navbarColor, setNavbarColor] = useNavbarColor();
    const [isHovering, setIsHovering] = useState(false);

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    // ** States
    const [isMounted, setIsMounted] = useState(false);
    const [menuVisibility, setMenuVisibility] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // ** Store Vars
    const dispatch = useDispatch();
    const layoutStore = useSelector((state) => state.layout);

    // ** Update Window Width
    const handleWindowWidth = () => {
        setWindowWidth(window.innerWidth);
    };

    // ** Vars
    const location = useLocation();
    const contentWidth = layoutStore.contentWidth;
    const menuCollapsed = layoutStore.menuCollapsed;
    const isHidden = layoutStore.menuHidden;

    // ** Toggles Menu Collapsed
    const setMenuCollapsed = (val) => dispatch(handleMenuCollapsed(val));

    // ** Handles Content Width
    const setContentWidth = (val) => dispatch(handleContentWidth(val));

    // ** Handles Content Width
    const setIsHidden = (val) => dispatch(handleMenuHidden(val));

    //** This function will detect the Route Change and will hide the menu on menu item click
    useEffect(() => {
        if (menuVisibility && windowWidth < 1200) {
            setMenuVisibility(false);
        }
    }, [location]);

    //** Sets Window Size & Layout Props
    useEffect(() => {
        if (window !== undefined) {
            window.addEventListener("resize", handleWindowWidth);
        }
    }, [windowWidth]);

    //** ComponentDidMount
    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    // ** Vars
    const footerClasses = {
        static: "footer-static",
        sticky: "footer-fixed",
        hidden: "footer-hidden",
    };

    const navbarWrapperClasses = {
        floating: "navbar-floating",
        sticky: "navbar-sticky",
        static: "navbar-static",
        hidden: "navbar-hidden",
    };

    const navbarClasses = {
        floating: "floating-nav",
        sticky: "fixed-top",
        static: "navbar-static-top",
        hidden: "d-none",
    };

    const trans = {
        width: "0px",
    };

    const bgColorCondition =
        navbarColor !== "" &&
        navbarColor !== "light" &&
        navbarColor !== "white";

    if (!isMounted) {
        return null;
    }
    return (
        <div
            className={classnames(
                `wrapper vertical-layout ${
                    navbarWrapperClasses[navbarType] || "navbar-floating"
                } ${footerClasses[footerType] || "footer-static"}`,
                {
                    // Modern Menu
                    "vertical-menu-modern": windowWidth >= 1200,
                    "menu-collapsed": menuCollapsed && windowWidth >= 1200,
                    "menu-expanded": !menuCollapsed && windowWidth > 1200,

                    // Overlay Menu
                    "vertical-overlay-menu": windowWidth < 1200,
                    "menu-hide": !menuVisibility && windowWidth < 1200,
                    "menu-open": menuVisibility && windowWidth < 1200,
                }
            )}
            {...(isHidden ? { "data-col": "1-column" } : {})}
        >
            {!isHidden ? (
                <SidebarComponent
                    skin={skin}
                    setSkin={setSkin}
                    menu={menu}
                    menuCollapsed={menuCollapsed}
                    menuVisibility={menuVisibility}
                    setMenuCollapsed={setMenuCollapsed}
                    setMenuVisibility={setMenuVisibility}
                    routerProps={routerProps}
                    currentActiveItem={currentActiveItem}
                />
            ) : null}

            <div className="row">
                <Navbar
                    expand="lg"
                    light={skin !== "dark"}
                    dark={skin === "dark" || bgColorCondition}
                    color={bgColorCondition ? navbarColor : undefined}
                    style={
                        windowWidth >= 1200 && !isHovering
                            ? {
                                  marginLeft: "28px",
                                  width:
                                      windowWidth >= 1780
                                          ? "40.9%"
                                          : windowWidth >= 1580
                                          ? "39.8%"
                                          : windowWidth >= 1480
                                          ? "38.7%"
                                          : windowWidth >= 1200 && "37.6%",
                                  transition: "all 0.3s ease-in-out",
                              }
                            : {
                                  marginLeft: windowWidth >= 1200 && "28px",
                                  transition:
                                      windowWidth >= 1200 &&
                                      "all 0.3s ease-in-out",
                              }
                    }
                    onMouseEnter={handleMouseEnter}
                    onFocus={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className={classnames(
                        `header-navbar navbar align-items-center  ${navbarClasses[navbarType]} navbar-shadow`,
                        {
                            "w-0": windowWidth <= 1200,
                            // "col-lg-5":windowWidth >= 1200,
                        }
                    )}
                >
                    <div className="navbar-container d-flex content">
                        {navbar ? (
                            navbar({ setMenuVisibility, skin, setSkin })
                        ) : (
                            <NavbarComponent
                                setMenuVisibility={setMenuVisibility}
                                skin={skin}
                                setSkin={setSkin}
                            />
                        )}
                    </div>
                </Navbar>
                <Link to={"/j33ves"}>
                    <div
                        className="d-none d-xl-block "
                        style={{
                            display: windowWidth < 1200 && "hidden",
                        }}
                    >
                        <Navbar
                            expand="lg"
                            light={skin !== "dark"}
                            dark={skin === "dark" || bgColorCondition}
                            color={bgColorCondition ? navbarColor : undefined}
                            style={{
                                marginRight: "28px",
                                width: isHovering
                                    ? "0%"
                                    : windowWidth >= 1780
                                    ? "40.9%"
                                    : windowWidth >= 1580
                                    ? "39.8%"
                                    : windowWidth >= 1480
                                    ? "38.7%"
                                    : windowWidth >= 1200 && "37.4%",
                                opacity: isHovering ? 0 : 1,
                                backgroundColor: "brown",
                                transition: "all 0.3s ease-in-out",
                            }}
                            className={classnames(
                                `header-navbar navbar align-items-center  ${navbarClasses[navbarType]} navbar-shadow`
                            )}
                        >
                            <div
                                className="navbar-container d-flex justify-content-between content"
                                style={{
                                    paddingBottom: "0px",
                                    position: "relative",
                                }}
                            >
                                <span
                                    style={{
                                        position: "absolute",
                                        backgroundColor: "black",
                                        color: "white",
                                        marginLeft: "-13px",
                                        fontSize: "6px",
                                        marginTop: "-10px",
                                        letterSpacing: "2px",
                                        paddingLeft: "2px",
                                        borderRadius: "3px",
                                    }}
                                >
                                    Advertisment
                                </span>
                                <img
                                    src={jeeves}
                                    alt="j33vse image"
                                    style={{ width: "84px", height: "56px" }}
                                ></img>
                                <div className="d-flex flex-col">
                                    <p
                                        style={{
                                            color: "yellow",
                                            marginLeft: "20px",
                                            // textAlign: "right",
                                            letterSpacing: "1.5px",
                                            opacity: isHovering ? 0 : 1,
                                            transition: "all 0.2s ease-in-out",
                                            textOverflow:
                                                isHovering && "ellipsis",
                                            overflow: isHovering && "hidden",
                                            marginBottom: 0,
                                            minWidth: "470px",
                                            fontSize: "16.4px",
                                        }}
                                        // className="font-small-3"
                                    >
                                        I will buy resources and resupply your
                                        fleets automatically.
                                        <span
                                            style={{
                                                textAlign: "right",
                                                color: "yellow",
                                                marginBottom: 0,
                                                marginLeft: "222px",
                                            }}
                                        >
                                            J33VES
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </Navbar>
                    </div>
                </Link>
            </div>
            {children}

            {/* Vertical Nav Menu Overlay */}
            <div
                className={classnames("sidenav-overlay", {
                    show: menuVisibility,
                })}
                onClick={() => setMenuVisibility(false)}
            ></div>
            {/* Vertical Nav Menu Overlay */}

            {themeConfig.layout.customizer === true ? (
                <Customizer
                    skin={skin}
                    setSkin={setSkin}
                    footerType={footerType}
                    setFooterType={setFooterType}
                    navbarType={navbarType}
                    setNavbarType={setNavbarType}
                    navbarColor={navbarColor}
                    setNavbarColor={setNavbarColor}
                    isRtl={isRtl}
                    setIsRtl={setIsRtl}
                    layout={props.layout}
                    setLayout={props.setLayout}
                    isHidden={isHidden}
                    setIsHidden={setIsHidden}
                    contentWidth={contentWidth}
                    setContentWidth={setContentWidth}
                    menuCollapsed={menuCollapsed}
                    setMenuCollapsed={setMenuCollapsed}
                    transition={props.transition}
                    setTransition={props.setTransition}
                    themeConfig={themeConfig}
                />
            ) : null}
            <footer
                className={classnames(
                    `footer footer-light ${
                        footerClasses[footerType] || "footer-static"
                    }`,
                    {
                        "d-none": footerType === "hidden",
                    }
                )}
            >
                {footer ? (
                    footer({ footerType, footerClasses })
                ) : (
                    <FooterComponent
                        footerType={footerType}
                        footerClasses={footerClasses}
                    />
                )}
            </footer>

            {themeConfig.layout.scrollTop === true ? (
                <div className="scroll-to-top">
                    <ScrollToTop showUnder={300} style={{ bottom: "5%" }}>
                        <Button className="btn-icon" color="primary">
                            <ArrowUp size={14} />
                        </Button>
                    </ScrollToTop>
                </div>
            ) : null}
        </div>
    );
};

export default VerticalLayout;
